import {useMemo, useState} from 'react';
import {trpc, formatTRPCError} from '@common-web/lib/trpc';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@common-web/components/ui/dialog';
import {Button} from '@common-web/components/ui/button';
import {Label} from '@common-web/components/ui/label';
import {Input} from '@common-web/components/ui/input';
import {Checkbox} from '@common-web/components/ui/checkbox';
import {Badge} from '@common-web/components/ui/badge';
import {Card, CardContent} from '@common-web/components/ui/card';
import {Check, X, AlertCircle, Loader2} from 'lucide-react';
import {useProductSelection} from '@client/hooks/useProductSelection';
import type {CatalogProduct} from '@client/hooks/useProductSelection';
import type {ImportedMember} from './types';

type AssignProductsPanelProps = {
  groupId: string;
  members: ImportedMember[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function AssignProductsPanel({
  groupId,
  members,
  open,
  onOpenChange,
}: AssignProductsPanelProps) {
  const {productCatalog, productsQuery, getProductName} = useProductSelection({groupId});

  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>(() =>
    members.map((m) => m.id),
  );
  const [effectiveDate, setEffectiveDate] = useState('');
  const [error, setError] = useState<string | null>(null);

  const memberNameById = useMemo(
    () => new Map(members.map((m) => [m.id, m.name])),
    [members],
  );

  const batchEnroll = trpc.group.vbaMembers.batchProductEnroll.useMutation({
    onError: (err) => setError(formatTRPCError(err)),
  });

  function toggleProduct(productId: string) {
    setSelectedProductIds((prev) =>
      prev.includes(productId) ? prev.filter((id) => id !== productId) : [...prev, productId],
    );
  }

  function toggleMember(memberId: string) {
    setSelectedMemberIds((prev) =>
      prev.includes(memberId) ? prev.filter((id) => id !== memberId) : [...prev, memberId],
    );
  }

  function handleApply() {
    setError(null);
    if (selectedProductIds.length === 0) {
      setError('Select at least one product.');
      return;
    }
    if (selectedMemberIds.length === 0) {
      setError('Select at least one member.');
      return;
    }
    batchEnroll.mutate({
      groupId,
      memberIds: selectedMemberIds,
      productIds: selectedProductIds,
      effectiveDate: effectiveDate || undefined,
    });
  }

  const result = batchEnroll.data;
  const resultsByMember = useMemo(() => {
    if (!result) return [];
    const grouped = new Map<string, typeof result.results>();
    for (const row of result.results) {
      const existing = grouped.get(row.memberId) ?? [];
      existing.push(row);
      grouped.set(row.memberId, existing);
    }
    return [...grouped.entries()].map(([memberId, rows]) => ({
      memberId,
      name: memberNameById.get(memberId) ?? memberId,
      rows,
    }));
  }, [result, memberNameById]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-3xl max-h-[85vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>Assign Products to Imported Members</DialogTitle>
        </DialogHeader>

        {!result ? (
          <div className='space-y-5'>
            <div className='space-y-2'>
              <Label className='text-xs font-medium text-gray-500 uppercase tracking-wider'>
                Products
              </Label>
              {productsQuery.isLoading ? (
                <div className='flex items-center gap-2 text-sm text-gray-400'>
                  <Loader2 className='h-4 w-4 animate-spin' /> Loading products…
                </div>
              ) : productCatalog.length === 0 ? (
                <p className='text-xs text-gray-400'>No products found for this group.</p>
              ) : (
                <div className='space-y-1'>
                  {productCatalog.map((p: CatalogProduct) => (
                    <label
                      key={p.id}
                      className='flex items-center gap-2 rounded-sm bg-gray-50 px-2 py-1.5 cursor-pointer'
                    >
                      <Checkbox
                        checked={selectedProductIds.includes(p.id)}
                        onCheckedChange={() => toggleProduct(p.id)}
                      />
                      <span className='text-sm text-gray-800'>
                        {p.name}
                        {p.productId ? (
                          <span className='text-xs text-gray-400 ml-1'>({p.productId})</span>
                        ) : null}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className='space-y-2'>
              <Label htmlFor='assignEffectiveDate' className='text-xs'>
                Effective date
              </Label>
              <div className='max-w-xs'>
                <Input
                  id='assignEffectiveDate'
                  type='date'
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                />
              </div>
              <p className='text-xs text-gray-400'>Defaults to today if left blank.</p>
            </div>

            <div className='space-y-2'>
              <div className='flex items-center justify-between'>
                <Label className='text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Members ({selectedMemberIds.length}/{members.length})
                </Label>
                <div className='flex gap-2'>
                  <Button
                    variant='ghost'
                    size='sm'
                    className='h-6 text-xs'
                    onClick={() => setSelectedMemberIds(members.map((m) => m.id))}
                  >
                    Select all
                  </Button>
                  <Button
                    variant='ghost'
                    size='sm'
                    className='h-6 text-xs'
                    onClick={() => setSelectedMemberIds([])}
                  >
                    Clear
                  </Button>
                </div>
              </div>
              <div className='max-h-52 overflow-y-auto space-y-1 border rounded-md p-2'>
                {members.map((m) => (
                  <label key={m.id} className='flex items-center gap-2 px-1 py-1 cursor-pointer'>
                    <Checkbox
                      checked={selectedMemberIds.includes(m.id)}
                      onCheckedChange={() => toggleMember(m.id)}
                    />
                    <span className='text-sm text-gray-800'>{m.name}</span>
                    <span className='text-xs text-gray-400 ml-auto font-mono'>{m.id}</span>
                  </label>
                ))}
              </div>
            </div>

            {error && (
              <div className='rounded-md bg-red-50 p-3 flex items-start gap-2'>
                <AlertCircle className='h-4 w-4 text-red-600 shrink-0 mt-0.5' />
                <p className='text-xs text-red-700'>{error}</p>
              </div>
            )}
          </div>
        ) : (
          <div className='space-y-4'>
            <div className='grid grid-cols-3 gap-3'>
              <div className='p-3 rounded-lg bg-gray-50 text-center'>
                <p className='text-xl font-bold text-brand-navy'>{result.summary.membersRequested}</p>
                <p className='text-xs text-gray-500'>Members</p>
              </div>
              <div className='p-3 rounded-lg bg-emerald-50 text-center'>
                <p className='text-xl font-bold text-emerald-600'>{result.summary.succeeded}</p>
                <p className='text-xs text-gray-500'>Enrollments OK</p>
              </div>
              <div
                className={`p-3 rounded-lg text-center ${
                  result.summary.failed > 0 ? 'bg-red-50' : 'bg-gray-50'
                }`}
              >
                <p
                  className={`text-xl font-bold ${
                    result.summary.failed > 0 ? 'text-red-600' : 'text-gray-600'
                  }`}
                >
                  {result.summary.failed}
                </p>
                <p className='text-xs text-gray-500'>Failed</p>
              </div>
            </div>

            <div className='space-y-2'>
              {resultsByMember.map((member) => (
                <Card key={member.memberId}>
                  <CardContent className='p-3'>
                    <p className='text-sm font-medium text-gray-900 mb-1'>{member.name}</p>
                    <div className='space-y-1'>
                      {member.rows.map((row) => (
                        <div
                          key={`${row.memberId}-${row.productId}`}
                          className='flex items-start gap-2 text-xs'
                        >
                          {row.ok ? (
                            <Badge className='bg-emerald-100 text-emerald-700'>
                              <Check className='h-3 w-3 mr-1' />
                              {getProductName(row.productId)}
                            </Badge>
                          ) : (
                            <>
                              <Badge className='bg-red-100 text-red-700 shrink-0'>
                                <X className='h-3 w-3 mr-1' />
                                {getProductName(row.productId)}
                              </Badge>
                              <span className='text-red-600'>{row.error}</span>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant='outline' onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleApply} disabled={batchEnroll.isPending}>
                {batchEnroll.isPending ? (
                  <>
                    <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                    Assigning…
                  </>
                ) : (
                  'Assign Products'
                )}
              </Button>
            </>
          ) : (
            <Button onClick={() => onOpenChange(false)}>Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
